import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import DateField from './DateField';
import TeamField from './TeamField';
import type { RegistrationCategory, RegistrationTeam } from '../../services/registrations';
import {
  trophyParticipantsService,
  type TrophyParticipant,
  type TrophyParticipantPayload,
} from '../../services/trophyParticipants';
import { isUnderConsentAge } from '../../utils/consent-age';
import GuardianEmailField from './GuardianEmailField';

/** Radix Select cannot hold an empty value, so "none chosen" carries a token. */
const NONE = '__none__';

const today = new Date();
const OLDEST_BIRTH_DATE = new Date(1920, 0, 1);

const EMPTY: TrophyParticipantPayload = {
  name: '',
  surname: '',
  email: '',
  guardian_email: '',
  birth_date: '',
  competition_category_id: '',
  competition_team_id: '',
  gender: '',
};

function fromParticipant(person: TrophyParticipant): TrophyParticipantPayload {
  return {
    name: person.name ?? '',
    surname: person.surname ?? '',
    email: person.email ?? '',
    guardian_email: person.guardian_email ?? '',
    birth_date: person.birth_date ?? '',
    competition_category_id: person.category_id ?? '',
    competition_team_id: person.team_id ?? '',
    gender: person.gender ?? '',
  };
}

interface TrophyParticipantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trophySlug: string;
  participant: TrophyParticipant | null;
  categories: RegistrationCategory[];
  teams: RegistrationTeam[];
  /**
   * Whether the form asks the gender of the person's class — the circuit's
   * agreement covers it. Corrected, like the date, at every stage at once.
   */
  genderCollected?: boolean;
  /** A squad written from inside the form, so the page's list grows with it. */
  onTeamCreated: (team: RegistrationTeam) => void;
  onSaved: (participant: TrophyParticipant) => void;
}

/**
 * Correcting one person for the whole circuit.
 *
 * Saving writes to **every** entry this person has across the stages — that is
 * the reason a circuit-level view exists, so a misspelt surname is fixed once
 * rather than in six places. The dialog says so, because "edit" usually means
 * "edit this one".
 *
 * No payment field: money is taken at a stage's desk, for that stage.
 */
export default function TrophyParticipantDialog({
  open,
  onOpenChange,
  trophySlug,
  participant,
  categories,
  teams,
  genderCollected = false,
  onTeamCreated,
  onSaved,
}: TrophyParticipantDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<TrophyParticipantPayload>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(participant ? fromParticipant(participant) : EMPTY);
  }, [open, participant]);

  const setField = (field: keyof TrophyParticipantPayload, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const minor = isUnderConsentAge(form.birth_date);
  const canSave =
    (form.name ?? '').trim().length > 0 && (!minor || (form.guardian_email ?? '').trim().length > 0);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!participant || !canSave || saving) return;

    // The gender only where the circuit's agreement covers it.
    const payload: TrophyParticipantPayload = { ...form };
    if (!genderCollected) delete payload.gender;

    setSaving(true);
    try {
      onSaved(await trophyParticipantsService.update(trophySlug, participant.id, payload));
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('gymAdmin.trophyParticipants.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.trophyParticipants.editTitle')}</DialogTitle>
          <DialogDescription>
            {t('gymAdmin.trophyParticipants.editDescription', {
              count: participant?.stages_count ?? 0,
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tp-name">{t('gymAdmin.members.fields.name')}</Label>
              <Input
                id="tp-name"
                value={form.name ?? ''}
                onChange={(e) => setField('name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tp-surname">{t('gymAdmin.members.fields.surname')}</Label>
              <Input
                id="tp-surname"
                value={form.surname ?? ''}
                onChange={(e) => setField('surname', e.target.value)}
              />
            </div>
          </div>

          {/* Straight after the name, as on the stage's entry form: it is what
              decides whose address is asked for next, and it settles the class.
              A circuit holds no tax code, no card number and no certificate
              expiry. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="tp-birth-date"
              label={t('gymAdmin.members.fields.birthDate')}
              value={form.birth_date ?? ''}
              onChange={(value) => setField('birth_date', value)}
              captionLayout="dropdown"
              startMonth={OLDEST_BIRTH_DATE}
              endMonth={today}
            />

            {/* The gender of their class, the person's like the date: one
                correction for every stage, and a class the strategy gave
                follows it at each. */}
            {genderCollected && (
              <div className="space-y-2">
                <Label htmlFor="tp-gender">{t('gymAdmin.registrations.gender')}</Label>
                <Select
                  value={form.gender || NONE}
                  onValueChange={(value) => setField('gender', value === NONE ? '' : value)}
                >
                  <SelectTrigger id="tp-gender" aria-label={t('gymAdmin.registrations.gender')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('gymAdmin.registrations.genderNotSet')}</SelectItem>
                    <SelectItem value="male">{t('gymAdmin.registrations.genders.male')}</SelectItem>
                    <SelectItem value="female">{t('gymAdmin.registrations.genders.female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Under 14 the address that matters is the guardian's; the child's
              own becomes optional. Same rule as the stage's entry form. */}
          {minor && (
            <GuardianEmailField
              id="tp-guardian-email"
              value={form.guardian_email ?? ''}
              onChange={(value) => setField('guardian_email', value)}
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="tp-email">
              {minor ? t('gymAdmin.registrations.athleteEmail') : t('gymAdmin.members.fields.email')}
            </Label>
            <Input
              id="tp-email"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setField('email', e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* The squad this person turns up with, for every stage at once. */}
            <TeamField
              id="tp-team"
              value={form.competition_team_id ?? ''}
              teams={teams}
              onChange={(teamId) => setField('competition_team_id', teamId)}
              ownerSlug={trophySlug}
              owner="trophy"
              onCreated={onTeamCreated}
            />

            {categories.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="tp-category">{t('gymAdmin.members.fields.category')}</Label>
                <Select
                  value={form.competition_category_id || NONE}
                  onValueChange={(value) =>
                    setField('competition_category_id', value === NONE ? '' : value)
                  }
                >
                  <SelectTrigger id="tp-category">
                    <SelectValue placeholder={t('gymAdmin.members.fields.noCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('gymAdmin.members.fields.noCategory')}</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
